import React from 'react';
import classNames from 'classnames';

import TermTooltip from '~components/TermTooltip';

import { FIGURE_PALETTE } from '../figurePalette';
import Pill from '../pill/Pill';

export type FigureTone = 'default' | 'danger' | 'warning';

export interface FigureCaption {
  /** Unique on the page: becomes the DOM id `term-tooltip-figure-{id}`. */
  id: string;
  content: React.ReactNode;
}

export interface FigureTileProps {
  label: string;
  value: React.ReactNode;
  /** One line under the value; the tooltip keeps the full explanation. */
  description?: React.ReactNode;
  /** Opt-in: the label becomes a TermTooltip. */
  caption?: FigureCaption;
  /** danger and warning colour the label and value, and the pill. */
  tone?: FigureTone;
  /** Short status text shown as a pill beside the value, in the tone colour (neutral when default). */
  pill?: string;
  dataCy?: string;
}

/** One figure: label, large value, optional status pill and a one-line description, as its own `<dl>`. */
const FigureTile: React.FC<FigureTileProps> = ({
  label,
  value,
  description,
  caption,
  tone = 'default',
  pill,
  dataCy = 'figure-tile',
}) => (
  <dl className={classNames('figure-tile', tone !== 'default' && tone)} data-cy={dataCy}>
    <dt className="figure-tile-label">
      {caption ? <TermTooltip id={`figure-${caption.id}`} label={label} content={caption.content} /> : label}
    </dt>
    <dd className="figure-tile-value-row">
      <span className="figure-tile-value">{value}</span>
      {pill && (
        <Pill text={pill} color={FIGURE_PALETTE[tone === 'default' ? 'neutral' : tone]} className="figure-tile-pill" />
      )}
    </dd>
    {description && <dd className="figure-tile-description">{description}</dd>}
  </dl>
);

export default FigureTile;
